// ABOUTME: The runner's frame: header, steersman strip, scene host with the screen's layer, and the plate, in that DOM order.
// ABOUTME: Evaluates the registered screen for the server's screen ID, handles exits, focus, overlays and the fallback plate.

import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import crossingTheme from '../../../theme/theme.crossing';
import { CROSSING_EXIT_OVERLAY } from '../../../theme/crossingTokens';
import '../crossing/crossingReducedMotion.css';
import { useReducedMotion, ReducedMotionContext } from '../crossing/useReducedMotion';
import CrossingHeader from '../crossing/CrossingHeader';
import SteersmanStrip from '../crossing/SteersmanStrip';
import CrossingDevLabel from '../crossing/CrossingDevLabel';
import SteersmanToggle from '../crossing/SteersmanToggle';
import { useSteersmanPreference } from '../crossing/useSteersmanPreference';
import api, { getCrossingChildPreferences, getCrossingChildPreferencesForChild } from '../../../services/api';
import { useCrossingContent, CrossingContentSourceContext } from '../../../hooks/useCrossingContent';
import type { CrossingChildPreferencesDto, CrossingDto, CrossingWithProgress } from '../../../types/api';
import { useCrossingRunner } from './useCrossingRunner';
import { SCREEN_REGISTRY } from './registry';
import SceneHost from './SceneHost';
import RunnerPlate from './RunnerPlate';
import { RUNNER_PLATE_CLEARANCE_PX, plateOverhangPx } from './plateClearance';
import FallbackPlate from './FallbackPlate';
import HistoryArrow from './HistoryArrow';
import type { LiveTier, RunnerApi, ScreenComponent, ScreenResult } from './screenContract';
import { ScreenMotionContext } from './screenMotion';
import { useSendOff } from './useSendOff';

const HEADLINE_ID = 'runner-headline';
const EXIT_LABEL = 'Save & Exit';
const LEAVE_WITH_UNSENT_PICKS = "You picked something that hasn't been saved yet. If you leave now, those picks won't be kept.";
const LEAVE_BUTTON = 'Leave anyway';
const KEEP_GOING_BUTTON = 'Keep going';
const START_ANOTHER_FAILED = "Couldn't start — try again";
const START_ANOTHER_PENDING = 'Starting another route…';
const START_ANOTHER_ACTION_ID = 'run_another';
const OVERLAY_TOP = '46px'; // below the header, which stays reachable over an overlay page
const OVERLAY_SCENE_Z = 4; // an overlay scene covers the cloud stack (1) and sits below the plate (10)
const EXIT_OVERLAY_Z = 1250; // above the plate and the header: the send-off's last fade covers everything
const LET_GO_ACTION_ID = 'let_go_together';
const READY_ACTION_ID = 'read_the_moment';
const CONFIRM_FEELING_ACTION_ID = 'confirm_feeling';
const TILT_AWAY_MS = 2000; // how long 5C's scenery stays up while it tilts away behind 5D's build-in

interface EvaluatorProps {
  screenId: string;
  Component: ScreenComponent;
  crossing: CrossingWithProgress;
  runner: RunnerApi;
  onResult: (screenId: string, result: ScreenResult) => void;
}

// Runs the screen function (which may call hooks) under a key of the screen ID, then lifts what it returned to the
// frame so the layer can sit inside the scene host and the plate outside it. Memoised: it re-runs only when its own
// inputs change, which is what keeps lifting the result from looping.
const ScreenEvaluator = memo(function ScreenEvaluator({ screenId, Component, crossing, runner, onResult }: EvaluatorProps) {
  const result = Component({ crossing, runner });
  useLayoutEffect(() => { onResult(screenId, result); });
  return null;
});

interface CrossingRunnerFrameProps {
  initialCrossing: CrossingDto;
  slug?: string;
  token?: string;
  // The dashboard child tab the parent opened this crossing from, handed back so the dashboard can restore it.
  returnToChildId?: number | null;
}

export default function CrossingRunnerFrame({ initialCrossing, slug, token, returnToChildId }: CrossingRunnerFrameProps) {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const contentSource = useMemo(() => ({ slug, token }), [slug, token]);
  const { data: content, refetch: refetchContent } = useCrossingContent(contentSource);
  const isChildMode = !!slug;

  const returnHref = isChildMode
    ? (token ? `/child-dashboard/${slug}?token=${encodeURIComponent(token)}` : `/child-dashboard/${slug}`)
    : '/';
  const returnState = useMemo(
    () => (!isChildMode && returnToChildId != null ? { activeChildId: returnToChildId } : undefined),
    [isChildMode, returnToChildId],
  );

  const leaveRef = useRef<() => void>(() => {});
  const runner = useCrossingRunner({
    crossingId: initialCrossing.id,
    slug,
    token,
    initialCrossing,
    onNotFound: () => leaveRef.current(),
    refetchContent,
  });
  const { progress, crossing, status, pressedActionId } = runner;

  const [rendered, setRendered] = useState<{ screenId: string; result: ScreenResult } | null>(null);
  const unsentRef = useRef(false);
  const handleResult = useCallback((screenId: string, result: ScreenResult) => {
    unsentRef.current = !!result.hasUnsentState;
    setRendered(previous => (previous && previous.screenId === screenId && sameResult(previous.result, result) ? previous : { screenId, result }));
  }, []);

  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const { cancel } = runner;
  // Set once the child has left, so a Reset that finishes starting afterwards does not pull them into a new crossing.
  const leftRef = useRef(false);
  useEffect(() => {
    leftRef.current = false;
    return () => { leftRef.current = true; };
  }, []);
  const leave = useCallback(() => {
    leftRef.current = true;
    cancel();
    navigate(returnHref, { replace: true, state: returnState });
  }, [cancel, navigate, returnHref, returnState]);
  useEffect(() => { leaveRef.current = leave; }, [leave]);

  const requestLeave = () => {
    if (unsentRef.current) setConfirmingLeave(true);
    else leave();
  };

  // Every exit but Save & Exit runs the send-off before it navigates (none under reduced motion). Run another route
  // starts a Reset the way the dashboard's Reset control does, as the send-off begins, and opens it when the Reset has
  // started and the send-off has run, whichever is later. While it starts, the plate shows the button busy and ignores
  // every exit; after it has started, a further press is ignored too, so one press never makes two crossings.
  const [exitNotice, setExitNotice] = useState<string | null>(null);
  const [startingAnother, setStartingAnother] = useState(false);
  const [plateHeight, setPlateHeight] = useState(0);
  const startedAnotherRef = useRef(false);
  const anotherCrossingIdRef = useRef<number | null>(null);
  const exitKindRef = useRef<'leave' | 'another'>('leave');
  const sendOffElapsedRef = useRef(false);
  const navigateToAnother = useCallback(() => {
    const crossingId = anotherCrossingIdRef.current;
    if (leftRef.current || crossingId === null || !sendOffElapsedRef.current || !slug) return;
    anotherCrossingIdRef.current = null;
    const query = token ? `?slug=${encodeURIComponent(slug)}&token=${encodeURIComponent(token)}` : `?slug=${encodeURIComponent(slug)}`;
    navigate(`/crossing/${crossingId}${query}`);
  }, [slug, token, navigate]);
  const sendOff = useSendOff({
    onElapsed: () => {
      sendOffElapsedRef.current = true;
      if (leftRef.current) return; // Save & Exit already left
      if (exitKindRef.current === 'another') navigateToAnother();
      else leave();
    },
  });
  const startAnother = useCallback(async () => {
    if (!slug) {
      console.error('Run another route needs a child crossing', { crossingId: initialCrossing.id });
      return;
    }
    if (startedAnotherRef.current) return;
    startedAnotherRef.current = true;
    setExitNotice(null);
    setStartingAnother(true);
    exitKindRef.current = 'another';
    sendOffElapsedRef.current = reducedMotion;
    if (!reducedMotion) sendOff.start();
    try {
      const response = await api.post<CrossingDto>(`/api/child/${slug}/crossings`, undefined, { params: token ? { token } : {} });
      if (leftRef.current) return;
      anotherCrossingIdRef.current = response.data.id;
      navigateToAnother();
    } catch (error) {
      console.error('Failed to start another crossing', { crossingId: initialCrossing.id, error });
      startedAnotherRef.current = false;
      if (leftRef.current) return;
      sendOff.reset();
      sendOffElapsedRef.current = false;
      setStartingAnother(false);
      setExitNotice(START_ANOTHER_FAILED);
    }
  }, [slug, token, initialCrossing.id, reducedMotion, sendOff, navigateToAnother]);

  // There is no Log screen yet, so Open the Log goes where Done goes: the dashboard, which lists the crossings.
  const handleExit = useCallback((name: string) => {
    if (sendOff.active || startedAnotherRef.current || leftRef.current) return;
    if (name === 'done' || name === 'open_log') {
      exitKindRef.current = 'leave';
      if (reducedMotion) leave();
      else sendOff.start();
    } else if (name === 'run_another') {
      void startAnother();
    } else {
      console.error('Unknown crossing exit', { exit: name, crossingId: initialCrossing.id });
    }
  }, [leave, startAnother, sendOff, reducedMotion, initialCrossing.id]);

  // 5D builds in only when it arrives from 5C by Let Go, Together. Any other way (a resume, a reload, history, a closed
  // crossing) draws the finished scene. Decided while rendering, on the change of screen, so the layer reads it at mount.
  // 2A likewise builds its deck and sea in only when it arrives from 1A by Ready for compass, after the scene has zoomed to it.
  // 2B builds its cloud stack only when it arrives from 2A by This feels close.
  const [arrival, setArrival] = useState({ screenId: progress.screenId, buildIn: false, tilting: false, deckBuildIn: false, stackBuildIn: false });
  if (arrival.screenId !== progress.screenId) {
    const buildIn = progress.screenId === '5D' && arrival.screenId === '5C' && pressedActionId === LET_GO_ACTION_ID && !reducedMotion;
    const deckBuildIn = progress.screenId === '2A' && arrival.screenId === '1A' && pressedActionId === READY_ACTION_ID && !reducedMotion;
    const stackBuildIn = progress.screenId === '2B' && arrival.screenId === '2A' && pressedActionId === CONFIRM_FEELING_ACTION_ID && !reducedMotion;
    setArrival({ screenId: progress.screenId, buildIn, tilting: buildIn, deckBuildIn, stackBuildIn });
  }
  useEffect(() => {
    if (!arrival.tilting) return;
    const timer = window.setTimeout(() => setArrival(previous => ({ ...previous, tilting: false })), TILT_AWAY_MS);
    return () => window.clearTimeout(timer);
  }, [arrival.tilting]);

  const screenMotion = useMemo(() => ({ buildIn: arrival.buildIn, sendOffStage: sendOff.stage }), [arrival.buildIn, sendOff.stage]);


  const Screen = SCREEN_REGISTRY[progress.screenId];
  const current = rendered && rendered.screenId === progress.screenId ? rendered.result : null;
  // For the one commit between a change of screen and the screen's result being lifted, the plate keeps drawing the
  // previous result (the layout effect re-renders before paint), so the alert and live region are never remounted.
  const plateResult = current ?? rendered?.result ?? null;
  // The same gap would unmount and remount the forgiven sky behind the clouds as 4B goes to 5B, so the scenery reads it from
  // the same bridged result.
  const sceneryResult = current ?? rendered?.result;
  const blocked = runner.saving || progress.closed;
  const overlayPage = progress.kind === 'overlay_page';
  const overlayScene = progress.kind === 'overlay_scene';
  const notBuiltYet = !Screen && /^[1-5][A-Z]$/.test(progress.screenId);

  const runnerApi = useMemo<RunnerApi>(() => ({ run: runner.run, back: runner.back, saving: runner.saving }), [runner.run, runner.back, runner.saving]);

  // Focus: the screen wrapper after a change of screen (including by back), the pressed button after a failed save.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const focusedScreenRef = useRef(progress.screenId);
  const hasWrapper = !!current || !Screen;
  useEffect(() => {
    if (!hasWrapper || focusedScreenRef.current === progress.screenId) return;
    focusedScreenRef.current = progress.screenId;
    const heading = wrapperRef.current?.querySelector<HTMLElement>('[data-overlay-heading]');
    (heading ?? wrapperRef.current)?.focus();
  }, [progress.screenId, hasWrapper]);

  useEffect(() => {
    if (status !== 'error' || !pressedActionId) return;
    document.querySelector<HTMLElement>(`[data-action-id="${pressedActionId}"]`)?.focus();
  }, [status, pressedActionId]);

  // The grown-up tips preference, loaded when the frame mounts. Until the load settles (answered or failed)
  // the tips stay hidden, so a grown-up who turned them off never sees them flash or has a view counted; a failed
  // load falls back to the strip's own defaults.
  const [preferences, setPreferences] = useState<CrossingChildPreferencesDto | null>(null);
  const [preferencesSettled, setPreferencesSettled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const fetchPreferences = isChildMode ? getCrossingChildPreferencesForChild(slug!, token) : getCrossingChildPreferences(crossing.childId);
    fetchPreferences.then(dto => {
      if (!cancelled) setPreferences(dto);
    }).catch(error => {
      console.error('Failed to load Crossing preferences', { crossingId: crossing.id, error });
      // Best-effort: the strip falls back to its own seenCount/override-null defaults on failure.
    }).finally(() => {
      if (!cancelled) setPreferencesSettled(true);
    });
    return () => { cancelled = true; };
  }, [crossing.childId, crossing.id, isChildMode, slug, token]);

  const { showSteersman, toggle: toggleSteersman } = useSteersmanPreference({
    crossingId: crossing.id,
    childId: crossing.childId,
    // An infinite count keeps the strip hidden until the load settles.
    seenCount: preferencesSettled ? (preferences?.steersmanSeenCount ?? 0) : Number.POSITIVE_INFINITY,
    override: preferences?.steersmanEnabled ?? null,
    slug,
    token,
  });

  const wrapper = (
    <Box
      key={`wrapper-${progress.screenId}`}
      ref={wrapperRef}
      data-screen-id={progress.screenId}
      tabIndex={-1}
      aria-labelledby={HEADLINE_ID}
      sx={{
        outline: 'none', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
        ...(overlayPage ? { position: 'fixed', inset: 0, top: OVERLAY_TOP, zIndex: 20, overflow: 'auto' } : {}),
        ...(overlayScene ? { position: 'absolute', inset: 0, zIndex: OVERLAY_SCENE_Z, overflow: 'auto' } : {}),
      }}
    >
      {current?.layer}
    </Box>
  );

  const screenBack = current?.back;
  const arrows = {
    backArrow: <HistoryArrow direction="back" target={screenBack ? null : progress.backTo} available={!!screenBack || progress.canGoBack} blocked={blocked} onPress={screenBack?.onPress ?? runner.back} />,
    // Forward carries no values, so it is not offered while the screen holds changes the server has not seen; the
    // screen's own forward button sends them.
    forwardArrow: <HistoryArrow direction="forward" target={progress.forwardTo} available={progress.canGoForward && !current?.hasUnsentState} blocked={blocked} onPress={runner.forward} />,
  };

  return (
    <ThemeProvider theme={crossingTheme}>
      <ReducedMotionContext.Provider value={reducedMotion}>
        <CrossingContentSourceContext.Provider value={contentSource}>
        <ScreenMotionContext.Provider value={screenMotion}>
        {/* The send-off's stage fade (0.6 s) and cover fade (1 s) are deliberate: the prototype declares both
            transitions but its frames cut, because its cover's display and opacity change in the same update,
            and the app plays the declared fade (#2212). */}
        <Box
          data-crossing-stage
          style={{ '--plate-overhang': `${plateOverhangPx(plateHeight)}px` } as CSSProperties}
          sx={{ pb: `${RUNNER_PLATE_CLEARANCE_PX}px`, minHeight: '100vh', display: 'flex', flexDirection: 'column', opacity: sendOff.stage >= 4 ? 0 : 1, transition: 'opacity .6s ease' }}
        >
          <CrossingDevLabel screenId={progress.screenId} name={progress.name} tips={content?.steersmanTips ?? {}} showSteersman={showSteersman} />
          <CrossingHeader
            crossingNumber={crossing.crossingNumber}
            childName={crossing.childName || `Child ${crossing.childId}`}
            returnHref={returnHref}
            returnState={returnState}
            exitLabel={EXIT_LABEL}
            onExit={requestLeave}
            rightSlot={preferencesSettled ? <SteersmanToggle showSteersman={showSteersman} onToggle={toggleSteersman} /> : undefined}
          />
          <SteersmanStrip screenId={progress.screenId} tips={content?.steersmanTips ?? {}} showSteersman={showSteersman} />
          <SceneHost key={progress.scene} scene={progress.scene} screenId={progress.screenId} crossing={crossing} cloudTiers={current?.cloudTiers} sceneSky={arrival.tilting ? 'forgiven' : sceneryResult?.sceneSky} sceneSkyFadesIn={sceneryResult?.sceneSkyFadesIn} tiltAway={arrival.tilting} deckBuildIn={arrival.deckBuildIn} stackBuildIn={arrival.stackBuildIn} inert={overlayPage}>
            {!overlayPage && wrapper}
          </SceneHost>
          {Screen ? (
            <>
              <ScreenEvaluator key={`evaluator-${progress.screenId}`} screenId={progress.screenId} Component={Screen} crossing={crossing} runner={runnerApi} onResult={handleResult} />
              {plateResult && (
                <RunnerPlate
                  progress={progress} result={plateResult} headlineId={HEADLINE_ID} status={startingAnother ? 'saving' : status} error={runner.error}
                  notice={exitNotice ?? runner.notice} announcement={exitNotice ?? (startingAnother ? START_ANOTHER_PENDING : runner.announcement)}
                  pressedActionId={startingAnother ? START_ANOTHER_ACTION_ID : pressedActionId}
                  onAction={runner.run} onExit={handleExit} onReload={() => window.location.reload()} inert={overlayPage} onHeight={setPlateHeight} {...arrows}
                />
              )}
              {overlayPage && wrapper}
            </>
          ) : (
            <FallbackPlate
              screenId={progress.screenId} notBuiltYet={notBuiltYet} canGoBack={progress.canGoBack} headlineId={HEADLINE_ID}
              notice={runner.notice} announcement={runner.announcement}
              onReload={() => window.location.reload()} onBack={runner.back} onDashboard={leave}
            />
          )}
        </Box>
        {sendOff.stage > 0 && (
          <Box
            aria-hidden="true"
            data-testid="crossing-exit-overlay"
            sx={{ position: 'fixed', inset: 0, zIndex: EXIT_OVERLAY_Z, pointerEvents: 'none', background: CROSSING_EXIT_OVERLAY, opacity: sendOff.stage >= 4 ? 1 : 0, transition: 'opacity 1s ease' }}
          />
        )}
        </ScreenMotionContext.Provider>
        </CrossingContentSourceContext.Provider>
        <Dialog open={confirmingLeave} onClose={() => setConfirmingLeave(false)} aria-labelledby="runner-leave-confirm">
          <DialogContent>
            <DialogContentText id="runner-leave-confirm">{LEAVE_WITH_UNSENT_PICKS}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmingLeave(false)}>{KEEP_GOING_BUTTON}</Button>
            <Button variant="contained" onClick={() => { setConfirmingLeave(false); leave(); }}>{LEAVE_BUTTON}</Button>
          </DialogActions>
        </Dialog>
      </ReducedMotionContext.Provider>
    </ThemeProvider>
  );
}

// Two results are the same when the parts the frame draws are: it keeps the lift from re-rendering on identical output.
function sameResult(a: ScreenResult, b: ScreenResult): boolean {
  return a.layer === b.layer && sameLiveTier(a.cloudTiers?.storm, b.cloudTiers?.storm) && sameLiveTier(a.cloudTiers?.weather, b.cloudTiers?.weather)
    && a.cloudTiers?.tiles === b.cloudTiers?.tiles && a.cloudTiers?.question === b.cloudTiers?.question
    && a.sceneSky === b.sceneSky && a.sceneSkyFadesIn === b.sceneSkyFadesIn && a.headline === b.headline && a.sub === b.sub && a.hasUnsentState === b.hasUnsentState
    && a.hideRail === b.hideRail && a.phaseVariant === b.phaseVariant && samePicker(a.picker, b.picker) && a.plateDimmed === b.plateDimmed && JSON.stringify(a.slots) === JSON.stringify(b.slots) && sameSlotPresses(a.slots, b.slots) && sameLocalActions(a.localActions, b.localActions)
    && a.back?.onPress === b.back?.onPress;
}

// JSON.stringify drops functions, so a slot's own press handler is compared by identity.
function sameSlotPresses(a: ScreenResult['slots'], b: ScreenResult['slots']): boolean {
  return [...new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])].every(id => a?.[id]?.onPress === b?.[id]?.onPress);
}

// A picker is the same when it shows the same name with the same arrow labels and handlers.
function samePicker(a: ScreenResult['picker'], b: ScreenResult['picker']): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.label === b.label && a.prevLabel === b.prevLabel && a.nextLabel === b.nextLabel && a.onPrev === b.onPrev && a.onNext === b.onNext;
}

// A live tier is the same when it draws the same item with the same handlers and labels.
function sameLiveTier(a: LiveTier<unknown> | undefined, b: LiveTier<unknown> | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.item === b.item && a.onPrev === b.onPrev && a.onNext === b.onNext
    && a.prevLabel === b.prevLabel && a.nextLabel === b.nextLabel;
}

// Local actions carry handlers, so they are compared by identity of the handler as well as by what is drawn.
function sameLocalActions(a: ScreenResult['localActions'], b: ScreenResult['localActions']): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((action, index) => {
    const other = b[index];
    return action.id === other.id && action.label === other.label && action.glyph === other.glyph && action.disabled === other.disabled
      && action.filled === other.filled && action.onPress === other.onPress;
  });
}
